import type { APInvoice } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';

interface InvoiceStatusBadgeProps {
  invoice: APInvoice;
  lineItemMatchSummary?: { total: number; matched: number };
}

export function InvoiceStatusBadge({
  invoice,
  lineItemMatchSummary,
}: InvoiceStatusBadgeProps) {
  const status = invoice.status;
  const summary = lineItemMatchSummary ?? invoice.lineItemMatchSummary;

  let matchDisplay = '';
  if (summary && summary.total > 0) {
    const { matched, total } = summary;
    matchDisplay = ` (${matched}/${total})`;
  }

  const isFullyMatched =
    summary && summary.total > 0 && summary.matched === summary.total;
  const isPartiallyMatched =
    summary &&
    summary.total > 0 &&
    summary.matched > 0 &&
    summary.matched < summary.total;
  const isUnmatched = summary && summary.total > 0 && summary.matched === 0;

  if (status === 'FINALIZED') {
    return (
      <Badge className="bg-gray-900 text-white hover:bg-gray-800">
        {status}
        {matchDisplay}
      </Badge>
    );
  }

  if (status === 'DRAFT') {
    if (isPartiallyMatched && summary) {
      const matchPercent = (summary.matched / summary.total) * 100;
      return (
        <Badge
          variant="secondary"
          className="border border-border"
          style={{
            background: `linear-gradient(to right, rgb(220 252 231) 0%, rgb(220 252 231) ${matchPercent}%, rgb(254 226 226) ${matchPercent}%, rgb(254 226 226) 100%)`,
          }}
        >
          {status}
          {matchDisplay}
        </Badge>
      );
    }
    if (isUnmatched) {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-200">
          {status}
          {matchDisplay}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        {status}
        {matchDisplay}
      </Badge>
    );
  }

  if (isFullyMatched) {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-200">
        {status}
        {matchDisplay}
      </Badge>
    );
  }

  if (isPartiallyMatched) {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200">
        {status}
        {matchDisplay}
      </Badge>
    );
  }

  return (
    <Badge variant="outline">
      {status}
      {matchDisplay}
    </Badge>
  );
}
